import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from 'react-query'
import { saveCatalogItem, getJobsForItem, loadCatalogItem, loadCatalogTemplates, loadItemCatalog, loadItemCategories, loadLaborItems, loadTags, restoreCatalogItem, retireCatalogItem, updateJobPricingForItem } from './api'
import { notification } from 'antd'
import { CatalogItem } from '../../domain/catalogItem'
import { CatalogItemFormValues } from './catalogItemModel'

export const useItemCatalogQuery = (searchQuery: string, pageSize: number, classIds: number[], categories: string[], retired: boolean = false) => {
  return useInfiniteQuery(
    ['item-catalog', { searchQuery, pageSize, classIds, categories, retired }],
    ({ pageParam = 1 }) => loadItemCatalog(searchQuery, pageParam, pageSize, classIds, categories, retired),
    {
      getNextPageParam: (_, pages) => pages.length + 1,
      staleTime: 60_000,
      refetchOnWindowFocus: false,
      onError: () => {
        notification.error({
          message: 'There was a problem loading the item catalog. Please try again after a few moments.'
        })
      }
    })
}

export const useLaborItemsQuery = (enabled: boolean) => {
  return useQuery(
    ['item-catalog', 'labor-items'],
    loadLaborItems,
    {
      enabled,
      refetchOnWindowFocus: false
    })
}

export const useInvalidateItemCatalog = () => {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries(['item-catalog'])
}

export const useItemCategories = (enabled: boolean) => {
  return useQuery({
    queryKey:['item-categories'], 
    queryFn: loadItemCategories,
    enabled,
    refetchOnWindowFocus: false
  })
}

export const useItemTags = (enabled: boolean) => {
  return useQuery({
    queryKey: ['item-tags'], 
    queryFn: loadTags, 
    enabled
  })
}

export const useCatalogTemplatesQuery = (searchQuery: string, pageSize: number, categories: string[]) => {
  return useInfiniteQuery(
    ['catalog-templates', { searchQuery, pageSize, categories }],
    ({ pageParam = 1 }) => loadCatalogTemplates(searchQuery, pageParam, pageSize, categories),
    {
      getNextPageParam: (_, pages) => pages.length + 1,
      staleTime: 60_000,
      refetchOnWindowFocus: false,
      onError: () => {
        notification.error({
          message: 'There was a problem loading the item catalog. Please try again after a few moments.'
        })
      }
    })
}

export const useSaveCatalogItemMutation = (onSuccess?: (catalogItem: CatalogItem) => void) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ catalogItem, updateAllJobs, jobIds }: { catalogItem: CatalogItemFormValues, updateAllJobs?: boolean, jobIds?: number[] }) => {
      const result = await saveCatalogItem(catalogItem)
      if (updateAllJobs !== undefined) {
        await updateJobPricingForItem(catalogItem.guid!, updateAllJobs, jobIds ?? [])
      }

      return result
    },
    onSuccess: (catalogItem) => {
      queryClient.invalidateQueries(['item-catalog'])
      queryClient.setQueryData(['catalog-item', catalogItem.id], catalogItem)
      onSuccess?.(catalogItem)
    }
  })
}

export const useCatalogItem = (id?: number, onSuccess?: (catalogItem: CatalogItem) => void) => {
  return useQuery({
    queryKey: ['catalog-item', id],
    queryFn: () => loadCatalogItem(id!),
    onSuccess,
    enabled: !!id,
  })
}

type UseRetireCatalogItemMutationOptions = {
  onSuccess?: () => void
  skipInvalidation?: boolean
}

export const useRetireCatalogItemMutation = (options?: UseRetireCatalogItemMutationOptions) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: retireCatalogItem,
    onSuccess: () => {
      if (!options?.skipInvalidation) {
        queryClient.invalidateQueries(['item-catalog'])
      }
      options?.onSuccess?.()
    }
  })
}

export const useRestoreCatalogItemMutation = (onSuccess?: (id: number) => void) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: restoreCatalogItem,
    onSuccess: (_, id) => {
      queryClient.invalidateQueries(['item-catalog'])
      onSuccess?.(id)
    }
  })
}

export const useJobsForItem = (itemId: number) => {
  return useQuery(['jobs-for-item', itemId], () => getJobsForItem(itemId), { enabled: !!itemId })
}
